import { type UpdatePrimaryCategoryInputData } from '../types/UpdatePrimaryCategoryInputData'
import { type UpdatePrimaryCategoryPayload } from '../types/UpdatePrimaryCategoryPayload'

/**
 * Maps input data to the update primary category API payload format.
 * This transformation is data access logic that converts camelCase to snake_case
 * as required by the API.
 *
 * @param inputData - Domain-level input data
 * @returns Payload formatted for the update API endpoint
 */
export const mapUpdatePrimaryCategoryInputDataToPayload = (
  inputData: UpdatePrimaryCategoryInputData
): UpdatePrimaryCategoryPayload => {
  return {
    name: inputData.name,
    email: inputData.email,
    phoneOne: inputData.phoneOne,
    fax: inputData.fax,
    url: inputData.url,
    external_id: inputData.externalId,
    logo: inputData.logo,
    locationTimezone: inputData.locationTimezone,
    language: inputData.language,
    currency: inputData.currency,
    // Omit the key when unset rather than sending an explicit null — the
    // backend requires legal_entity when multi-entity is on and strips it
    // when off, but null is never a valid reference
    ...(inputData.legalEntity != null
      ? { legal_entity: inputData.legalEntity }
      : {}),
    primary_billing_address: {
      id: inputData.primaryBillingAddress.id,
      name: inputData.primaryBillingAddress.name,
      addressLineOne: inputData.primaryBillingAddress.addressLineOne,
      city: inputData.primaryBillingAddress.city,
      state_province: inputData.primaryBillingAddress.stateProvince,
      postalCode: inputData.primaryBillingAddress.postalCode,
      country: inputData.primaryBillingAddress.country,
    },
    primary_shipping_address: {
      id: inputData.primaryShippingAddress.id,
      name: inputData.primaryShippingAddress.name,
      addressLineOne: inputData.primaryShippingAddress.addressLineOne,
      city: inputData.primaryShippingAddress.city,
      state_province: inputData.primaryShippingAddress.stateProvince,
      postalCode: inputData.primaryShippingAddress.postalCode,
      country: inputData.primaryShippingAddress.country,
    },
    shipping_addresses: inputData.shippingAddresses?.map((addr) => ({
      id: addr.id,
      name: addr.name,
      addressLineOne: addr.addressLineOne,
      city: addr.city,
      state_province: addr.stateProvince,
      postalCode: addr.postalCode,
      country: addr.country,
    })),
  }
}
